// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import Icon, { ICONS } from './icons';

export interface PromptOption {
  id: string;
  title: string;
  description: string;
  is_enabled: boolean;
}

interface SearchablePromptSelectProps {
  value?: string | null;
  onChange: (promptId: string | null) => void;
  prompts: PromptOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export default function SearchablePromptSelect({
  value,
  onChange,
  prompts,
  placeholder = "Select workflow prompt...",
  disabled = false,
  className = ""
}: SearchablePromptSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset search when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      setHighlightedIndex(-1);
    }
  }, [isOpen]);

  // Filter prompts based on search term
  const filteredPrompts = prompts.filter(prompt => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      prompt.title.toLowerCase().includes(searchLower) ||
      prompt.description.toLowerCase().includes(searchLower)
    );
  });

  // Get selected prompt for display
  const selectedPrompt = prompts.find(p => p.id === value);

  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!isOpen) {
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
        event.preventDefault();
        setIsOpen(true);
        setTimeout(() => inputRef.current?.focus(), 0);
      }
      return;
    }

    switch (event.key) {
      case 'Escape':
        setIsOpen(false);
        break;
      case 'ArrowDown':
        event.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredPrompts.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : prev);
        break;
      case 'Enter':
        event.preventDefault();
        if (highlightedIndex >= 0 && filteredPrompts[highlightedIndex]) {
          handleSelect(filteredPrompts[highlightedIndex].id);
        }
        break;
    }
  };

  const handleSelect = (promptId: string | null) => {
    onChange(promptId);
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    onChange(null);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Main Select Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={`
          w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded text-white text-left
          focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none
          disabled:opacity-50 disabled:cursor-not-allowed
          flex items-center justify-between
        `}
      >
        <span className={selectedPrompt ? "text-white" : "text-gray-400"}>
          {selectedPrompt ? (
            <div className="flex flex-col">
              <span className="font-medium">{selectedPrompt.title}</span>
              {selectedPrompt.description && (
                <span className="text-xs text-gray-400 truncate mt-0.5 max-w-xs">
                  {selectedPrompt.description}
                </span>
              )}
            </div>
          ) : (
            placeholder
          )}
        </span>
        
        <div className="flex items-center gap-1">
          {selectedPrompt && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 hover:bg-gray-700 rounded text-gray-400 hover:text-white transition-colors"
              title="Clear selection"
            >
              <Icon name={ICONS.X} size={14} className="text-current" />
            </button>
          )}
          <Icon 
            name={ICONS.CHEVRON_LEFT} 
            size={16} 
            className={`text-gray-400 transition-transform duration-200 ${isOpen ? '-rotate-90' : 'rotate-90'}`} 
          />
        </div>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-xl max-h-64 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-600">
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setHighlightedIndex(-1);
              }}
              onKeyDown={handleKeyDown}
              placeholder="Search prompts..."
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:border-purple-500 focus:ring-1 focus:ring-purple-500/20 focus:outline-none text-sm"
            />
          </div>

          {/* Options List */}
          <div className="max-h-48 overflow-y-auto">
            {filteredPrompts.length > 0 ? (
              <>
                {/* Clear option */}
                <button
                  type="button"
                  onClick={() => handleSelect(null)}
                  className={`
                    w-full px-3 py-2 text-left hover:bg-gray-700 transition-colors
                    text-gray-400 text-sm italic border-b border-gray-600
                  `}
                >
                  <Icon name={ICONS.X} size={14} className="inline mr-2" />
                  No prompt selected
                </button>
                
                {filteredPrompts.map((prompt, index) => (
                  <button
                    key={prompt.id}
                    type="button"
                    onClick={() => handleSelect(prompt.id)}
                    className={`
                      w-full px-3 py-2 text-left hover:bg-gray-700 transition-colors
                      ${highlightedIndex === index ? 'bg-gray-700' : ''}
                      ${prompt.id === value ? 'bg-purple-900/30 border-l-2 border-purple-500' : ''}
                      ${!prompt.is_enabled ? 'opacity-60' : ''}
                    `}
                  >
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium">{prompt.title}</span>
                        {!prompt.is_enabled && (
                          <span className="text-xs px-1.5 py-0.5 bg-red-900/50 text-red-300 rounded">
                            Disabled
                          </span>
                        )}
                      </div>
                      {prompt.description && (
                        <span className="text-xs text-gray-400 mt-0.5 line-clamp-2">
                          {prompt.description}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </>
            ) : (
              <div className="px-3 py-4 text-center text-gray-400 text-sm">
                <Icon name={ICONS.SEARCH} size={20} className="mx-auto mb-2 text-gray-500" />
                No prompts found matching "{searchTerm}"
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}